import { useAuthStore } from '../stores/auth';
import type { ApiResponse, PaginatedResponse } from '@work-log/shared';

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = useAuthStore.getState().token;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    if (response.status === 401) {
      useAuthStore.getState().logout();
    }
    throw new ApiError(response.status, data.error || 'Request failed');
  }

  return data;
}

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    request<ApiResponse<{ token: string; user: any }>>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  me: () => request<ApiResponse<any>>('/auth/me'),

  logout: () =>
    request<ApiResponse<void>>('/auth/logout', { method: 'POST' }),
};

// Requests API
export const requestsApi = {
  list: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return request<ApiResponse<PaginatedResponse<any>>>(`/requests${query}`);
  },

  get: (id: string | number) =>
    request<ApiResponse<any>>(`/requests/${id}`),

  create: (data: any) =>
    request<ApiResponse<any>>('/requests', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string | number, data: any) =>
    request<ApiResponse<any>>(`/requests/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  updateStatus: (id: string | number, status: string) =>
    request<ApiResponse<void>>(`/requests/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  delete: (id: string | number) =>
    request<ApiResponse<void>>(`/requests/${id}`, { method: 'DELETE' }),

  duplicate: (id: string | number) =>
    request<ApiResponse<any>>(`/requests/${id}/duplicate`, { method: 'POST' }),

  updateQuoteStatus: (id: string | number, quoteStatus: string) =>
    request<ApiResponse<void>>(`/requests/${id}/quote`, {
      method: 'PATCH',
      body: JSON.stringify({ quoteStatus }),
    }),

  // Work Logs
  getLogs: (requestId: string | number) =>
    request<ApiResponse<any[]>>(`/requests/${requestId}/logs`),

  addLog: (requestId: string | number, data: any) =>
    request<ApiResponse<any>>(`/requests/${requestId}/logs`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Comments
  getComments: (requestId: string | number) =>
    request<ApiResponse<any[]>>(`/requests/${requestId}/comments`),

  addComment: (requestId: string | number, content: string) =>
    request<ApiResponse<any>>(`/requests/${requestId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),
};

// Work Logs API
export const workLogsApi = {
  update: (id: string | number, data: any) =>
    request<ApiResponse<any>>(`/logs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string | number) =>
    request<ApiResponse<void>>(`/logs/${id}`, { method: 'DELETE' }),
};

// Comments API
export const commentsApi = {
  update: (id: string | number, content: string) =>
    request<ApiResponse<any>>(`/comments/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),

  delete: (id: string | number) =>
    request<ApiResponse<void>>(`/comments/${id}`, { method: 'DELETE' }),
};

// Dashboard API
export const dashboardApi = {
  stats: () => request<ApiResponse<any>>('/dashboard/stats'),

  timeUsage: (year?: number, month?: number) => {
    const params = new URLSearchParams();
    if (year) params.set('year', String(year));
    if (month) params.set('month', String(month));
    const query = params.toString() ? `?${params}` : '';
    return request<ApiResponse<any>>(`/dashboard/time-usage${query}`);
  },

  myTasks: () => request<ApiResponse<any[]>>('/dashboard/my-tasks'),

  recent: (limit = 5) =>
    request<ApiResponse<any[]>>(`/dashboard/recent?limit=${limit}`),

  warrantyExpiring: () =>
    request<ApiResponse<any[]>>('/dashboard/warranty-expiring'),

  pendingQuotes: () =>
    request<ApiResponse<any[]>>('/dashboard/pending-quotes'),
};

// Users API
export const usersApi = {
  list: () => request<ApiResponse<any[]>>('/users'),

  get: (id: string | number) =>
    request<ApiResponse<any>>(`/users/${id}`),
};

// Reports API
export const reportsApi = {
  monthly: (year?: number, month?: number) => {
    const params = new URLSearchParams();
    if (year) params.set('year', String(year));
    if (month) params.set('month', String(month));
    const query = params.toString() ? `?${params}` : '';
    return request<ApiResponse<any>>(`/reports/monthly${query}`);
  },

  trend: (months = 6) =>
    request<ApiResponse<any[]>>(`/reports/trend?months=${months}`),

  sla: (year?: number, month?: number) => {
    const params = new URLSearchParams();
    if (year) params.set('year', String(year));
    if (month) params.set('month', String(month));
    const query = params.toString() ? `?${params}` : '';
    return request<ApiResponse<any>>(`/reports/sla${query}`);
  },

  closeMonth: (year: number, month: number) =>
    request<ApiResponse<any>>('/reports/monthly/close', {
      method: 'PUT',
      body: JSON.stringify({ year, month }),
    }),
};

export { ApiError };
